/**
 * Timing and token counts for each response: how long it took, how many tokens
 * went in and came out, and how fast they came. Useful for comparing models,
 * and especially local ones, where speed depends on the machine.
 *
 * Providers report these in different shapes, or not at all. What ChainForge
 * always knows is how long its own request took; the rest is read from the
 * provider's reply where it has it.
 */
import {
  Dict,
  EvaluationResults,
  LLMResponseData,
  ResponseStats,
} from "./typing";
import {
  getProvider,
  isOpenRouterImageModel,
  LLM,
  LLMProvider,
  stripHuggingFacePrefix,
  stripOpenRouterPrefix,
} from "./models";
import {
  EnergyRange,
  estimateEnergyWh,
  findModel,
} from "./ecologits/ecologits";

/**
 * The key under which call functions that send one request per response record
 * that request's wall-clock time on its raw reply, for `extract_stats` to read.
 */
export const LATENCY_KEY = "__cf_latency_ms";

/**
 * The key under which call functions put a request's measured energy on its
 * raw reply (see localEnergy.ts), for `extract_stats` to read.
 */
export const ENERGY_KEY = "__cf_energy";

/**
 * The metavars each response's stats are exposed under, e.g. to code evaluators
 * as `response.meta["stat_tokens_per_s"]`. The prefix keeps them from colliding
 * with metavars of the user's own, which stats would otherwise overwrite.
 */
export const STATS_METAVARS = {
  latency_s: "stat_latency_s",
  ttft_s: "stat_ttft_s",
  input_tokens: "stat_input_tokens",
  output_tokens: "stat_output_tokens",
  tokens_per_s: "stat_tokens_per_s",
  decode_tokens_per_s: "stat_decode_tokens_per_s",
  averaged_over: "stat_averaged_over",
  cost_usd: "stat_cost_usd",
  energy_wh: "stat_energy_wh",
  load_energy_wh: "stat_load_energy_wh",
  power_source: "stat_power_source",
  power_mode: "stat_power_mode",
  est_energy_wh_min: "stat_est_energy_wh_min",
  est_energy_wh_max: "stat_est_energy_wh_max",
} as const;

const STATS_METAVAR_NAMES = new Set<string>(Object.values(STATS_METAVARS));

/** Whether a metavar holds a response's stats (and so belongs to that response alone). */
export function isStatsMetavar(name: string): boolean {
  return STATS_METAVAR_NAMES.has(name);
}

const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : undefined;

const firstNum = (...vals: unknown[]): number | undefined => {
  for (const v of vals) {
    const n = num(v);
    if (n !== undefined) return n;
  }
  return undefined;
};

const NS_PER_MS = 1e6;

/**
 * Token counts, timings and speed from one raw reply, in whichever shape its
 * provider uses, before rounding. For a reply with several choices, the counts
 * are the whole reply's; see `extract_stats` for per-response stats.
 */
export function statsFromReply(reply: Dict): ResponseStats {
  // Some providers' replies are stored with the provider's own reply under `raw`
  const r: Dict =
    reply?.raw && typeof reply.raw === "object"
      ? { ...reply.raw, ...reply }
      : reply;
  const usage: Dict = r?.usage ?? {};
  const meta: Dict = r?.usageMetadata ?? {}; // Gemini
  const timings: Dict = r?.timings ?? {}; // llama.cpp server
  const extra: Dict = usage?.extra ?? {}; // WebLLM

  const stats: ResponseStats = {};

  const input = firstNum(
    usage.prompt_tokens,
    usage.input_tokens,
    usage.inputTokens, // Bedrock
    meta.promptTokenCount,
    r?.prompt_eval_count, // Ollama
  );
  if (input !== undefined) stats.input_tokens = input;

  const geminiOutput =
    num(meta.candidatesTokenCount) !== undefined
      ? meta.candidatesTokenCount + (num(meta.thoughtsTokenCount) ?? 0)
      : undefined;
  const output = firstNum(
    usage.completion_tokens,
    usage.output_tokens,
    usage.outputTokens,
    geminiOutput,
    r?.eval_count,
  );
  if (output !== undefined) stats.output_tokens = output;

  // What the request cost, where the provider says (OpenRouter, for any model it serves)
  const cost = num(usage.cost);
  if (cost !== undefined) stats.cost_usd = cost;

  const latency = firstNum(
    r?.[LATENCY_KEY],
    r?.metrics?.latencyMs, // Bedrock
    num(r?.total_duration) !== undefined
      ? r.total_duration / NS_PER_MS
      : undefined,
  );
  if (latency !== undefined) stats.latency_ms = latency;

  // Time before output: Ollama reports loading the model and reading the prompt,
  // llama.cpp reading the prompt, and WebLLM its time to first token.
  if (
    num(r?.load_duration) !== undefined ||
    num(r?.prompt_eval_duration) !== undefined
  )
    stats.ttft_ms =
      ((num(r?.load_duration) ?? 0) + (num(r?.prompt_eval_duration) ?? 0)) /
      NS_PER_MS;
  else if (num(timings.prompt_ms) !== undefined)
    stats.ttft_ms = timings.prompt_ms;
  else if (num(extra.time_to_first_token_s) !== undefined)
    stats.ttft_ms = extra.time_to_first_token_s * 1000;

  // Decoding speed, where the server measures it itself
  const decodeSpeed = firstNum(
    num(r?.eval_count) !== undefined && num(r?.eval_duration)
      ? r.eval_count / (r.eval_duration / 1e9)
      : undefined,
    timings.predicted_per_second,
    extra.decode_tokens_per_s,
  );
  if (decodeSpeed !== undefined) stats.decode_tokens_per_s = decodeSpeed;

  // Energy measured on this machine, for a local model (see localEnergy.ts)
  const energy: Dict = r?.[ENERGY_KEY] ?? {};
  if (num(energy.energy_wh) !== undefined) {
    stats.energy_wh = energy.energy_wh;
    if (num(energy.noise_wh) !== undefined)
      stats.energy_noise_wh = energy.noise_wh;
    if (energy.components_wh && typeof energy.components_wh === "object")
      stats.energy_parts_wh = Object.fromEntries(
        Object.entries(energy.components_wh as Dict).filter(
          ([, v]) => num(v) !== undefined,
        ),
      );
    if (num(energy.load_energy_wh) !== undefined)
      stats.load_energy_wh = energy.load_energy_wh;
    if (energy.shared === true) stats.energy_shared = true;
    if (energy.conditions && typeof energy.conditions === "object") {
      const conditions = Object.fromEntries(
        Object.entries(energy.conditions as Dict).filter(
          ([, v]) => typeof v === "string" && v.length > 0,
        ),
      );
      if (Object.keys(conditions).length > 0)
        stats.energy_conditions = conditions;
    }
    if (energy.conditions_changed === true)
      stats.energy_conditions_changed = true;
    if (energy.baseline_before_change === true)
      stats.energy_baseline_before_change = true;
    if (energy.includes_load === true) stats.energy_includes_load = true;
  }

  return stats;
}

/** Rounds stats for storage, and works out speed from tokens and latency. */
function finish(stats: ResponseStats): ResponseStats | null {
  const res: ResponseStats = {};
  if (stats.latency_ms !== undefined)
    res.latency_ms = Math.round(stats.latency_ms);
  if (stats.ttft_ms !== undefined) res.ttft_ms = Math.round(stats.ttft_ms);
  if (stats.input_tokens !== undefined)
    res.input_tokens = Math.round(stats.input_tokens);
  if (stats.output_tokens !== undefined)
    res.output_tokens = Math.round(stats.output_tokens);
  const round1 = (x: number) => Math.round(x * 10) / 10;
  if (
    stats.output_tokens !== undefined &&
    stats.latency_ms !== undefined &&
    stats.latency_ms > 0
  )
    res.tokens_per_s = round1(stats.output_tokens / (stats.latency_ms / 1000));
  if (stats.decode_tokens_per_s !== undefined)
    res.decode_tokens_per_s = round1(stats.decode_tokens_per_s);
  // Requests can cost fractions of a cent, so keep 9 significant digits
  if (stats.cost_usd !== undefined)
    res.cost_usd = Number(stats.cost_usd.toPrecision(9));
  if (stats.est_energy_wh !== undefined)
    res.est_energy_wh = {
      min: Number(stats.est_energy_wh.min.toPrecision(3)),
      max: Number(stats.est_energy_wh.max.toPrecision(3)),
    };
  const sig = (x: number, digits: number) => Number(x.toPrecision(digits));
  if (stats.energy_wh !== undefined) res.energy_wh = sig(stats.energy_wh, 3);
  if (stats.energy_noise_wh !== undefined)
    res.energy_noise_wh = sig(stats.energy_noise_wh, 2);
  if (stats.energy_parts_wh !== undefined)
    res.energy_parts_wh = Object.fromEntries(
      Object.entries(stats.energy_parts_wh).map(([c, v]) => [c, sig(v, 3)]),
    );
  if (stats.load_energy_wh !== undefined)
    res.load_energy_wh = sig(stats.load_energy_wh, 3);
  if (stats.energy_shared) res.energy_shared = true;
  if (stats.energy_conditions) res.energy_conditions = stats.energy_conditions;
  if (stats.energy_conditions_changed) res.energy_conditions_changed = true;
  if (stats.energy_baseline_before_change)
    res.energy_baseline_before_change = true;
  if (stats.energy_includes_load) res.energy_includes_load = true;
  if (Object.keys(res).length === 0) return null;
  if (stats.averaged_over !== undefined && stats.averaged_over > 1)
    res.averaged_over = stats.averaged_over;
  return res;
}

/** Estimates one request's energy in Wh from its output tokens and latency. */
export type EnergyEstimator = (
  outputTokens: number,
  latencyMs: number,
) => EnergyRange | undefined;

/**
 * Each response's stats, in the same order as `extract_responses`.
 *
 * @param response The raw reply from `call_llm`: one reply, or one per request.
 * @param elapsed_ms How long the call to `call_llm` took, in total.
 * @param count How many responses were extracted from the reply.
 * @param estimateEnergy Estimates a request's energy from its output tokens
 * and latency, where the model has an estimate (see `energyEstimator`).
 *
 * A reply with several choices (one request asked for n responses) gives each
 * the request's latency, the average of its output tokens, and the speed that
 * average makes. Several replies (a request per response) each count on their
 * own; without a per-request time, each gets the average. Averaged stats are
 * marked with `averaged_over`. A request's energy is estimated once, for the
 * whole request, and shared between its responses like its cost.
 */
export function extract_stats(
  response: unknown,
  elapsed_ms: number | undefined,
  count: number,
  estimateEnergy?: EnergyEstimator,
): (ResponseStats | null)[] | undefined {
  if (count <= 0) return undefined;

  const replies: Dict[] = Array.isArray(response)
    ? (response as unknown[]).map((r) =>
        r !== null && typeof r === "object" ? (r as Dict) : {},
      )
    : response !== null && typeof response === "object"
      ? [response as Dict]
      : [];

  // WebLLM puts each completion's usage beside its choices.
  if (
    replies.length === 1 &&
    Array.isArray(replies[0]?.usages) &&
    replies[0].usages.length === count
  )
    return (replies[0].usages as Dict[]).map((u) =>
      finish(statsFromReply({ usage: u, [LATENCY_KEY]: u?.[LATENCY_KEY] })),
    );

  // How many responses each reply holds
  const sizes = replies.map((r) =>
    Array.isArray(r?.choices) && r.choices.length > 0 ? r.choices.length : 1,
  );
  const total = sizes.reduce((a, b) => a + b, 0);

  // When the replies don't line up with the responses (e.g. a custom provider
  // returning plain strings), all that's known is the total time.
  if (replies.length === 0 || total !== count) {
    if (elapsed_ms === undefined) return undefined;
    return Array.from({ length: count }, () =>
      finish({ latency_ms: elapsed_ms / count, averaged_over: count }),
    );
  }

  const stats: (ResponseStats | null)[] = [];
  replies.forEach((reply, i) => {
    const s = statsFromReply(reply);
    // Without its own time, a reply gets the average of the call's
    const latencyAveraged =
      s.latency_ms === undefined &&
      elapsed_ms !== undefined &&
      replies.length > 1;
    if (s.latency_ms === undefined && elapsed_ms !== undefined)
      s.latency_ms = elapsed_ms / replies.length;
    const k = sizes[i];
    const energy =
      estimateEnergy &&
      s.output_tokens !== undefined &&
      s.latency_ms !== undefined
        ? estimateEnergy(s.output_tokens, s.latency_ms)
        : undefined;
    for (let j = 0; j < k; j++)
      stats.push(
        finish({
          ...s,
          output_tokens:
            s.output_tokens !== undefined ? s.output_tokens / k : undefined,
          // One request's cost, shared between the responses it returned
          cost_usd: s.cost_usd !== undefined ? s.cost_usd / k : undefined,
          est_energy_wh: energy && { min: energy.min / k, max: energy.max / k },
          // Measured energy too (one reply per response for Ollama, so k is 1)
          energy_wh: s.energy_wh !== undefined ? s.energy_wh / k : undefined,
          energy_noise_wh:
            s.energy_noise_wh !== undefined ? s.energy_noise_wh / k : undefined,
          energy_parts_wh:
            s.energy_parts_wh &&
            Object.fromEntries(
              Object.entries(s.energy_parts_wh).map(([c, v]) => [c, v / k]),
            ),
          load_energy_wh:
            s.load_energy_wh !== undefined ? s.load_energy_wh / k : undefined,
          // A server-measured speed is per sequence already
          decode_tokens_per_s: k === 1 ? s.decode_tokens_per_s : undefined,
          averaged_over: Math.max(k, latencyAveraged ? replies.length : 1),
        }),
      );
  });
  return stats.some((s) => s !== null) ? stats : undefined;
}

/** The stats of the response at `index`, if it has any. */
export function statsAt(
  resp_obj: { stats?: (ResponseStats | null)[] },
  index: number,
): ResponseStats | undefined {
  return resp_obj.stats?.[index] ?? undefined;
}

/** A response's stats as metavars (see STATS_METAVARS), in seconds rather than milliseconds. */
export function statsToMetavars(
  stats: ResponseStats | undefined,
): Dict<LLMResponseData> {
  if (!stats) return {};
  const res: Dict<LLMResponseData> = {};
  const secs = (ms: number) => Math.round(ms) / 1000;
  if (stats.latency_ms !== undefined)
    res[STATS_METAVARS.latency_s] = secs(stats.latency_ms);
  if (stats.ttft_ms !== undefined)
    res[STATS_METAVARS.ttft_s] = secs(stats.ttft_ms);
  if (stats.input_tokens !== undefined)
    res[STATS_METAVARS.input_tokens] = stats.input_tokens;
  if (stats.output_tokens !== undefined)
    res[STATS_METAVARS.output_tokens] = stats.output_tokens;
  if (stats.tokens_per_s !== undefined)
    res[STATS_METAVARS.tokens_per_s] = stats.tokens_per_s;
  if (stats.decode_tokens_per_s !== undefined)
    res[STATS_METAVARS.decode_tokens_per_s] = stats.decode_tokens_per_s;
  if (stats.averaged_over !== undefined)
    res[STATS_METAVARS.averaged_over] = stats.averaged_over;
  if (stats.cost_usd !== undefined)
    res[STATS_METAVARS.cost_usd] = stats.cost_usd;
  if (stats.energy_wh !== undefined)
    res[STATS_METAVARS.energy_wh] = stats.energy_wh;
  if (stats.load_energy_wh !== undefined)
    res[STATS_METAVARS.load_energy_wh] = stats.load_energy_wh;
  if (stats.energy_conditions?.power_source)
    res[STATS_METAVARS.power_source] = stats.energy_conditions.power_source;
  if (stats.energy_conditions?.power_mode)
    res[STATS_METAVARS.power_mode] = stats.energy_conditions.power_mode;
  if (stats.est_energy_wh !== undefined) {
    res[STATS_METAVARS.est_energy_wh_min] = stats.est_energy_wh.min;
    res[STATS_METAVARS.est_energy_wh_max] = stats.est_energy_wh.max;
  }
  return res;
}

/**
 * A short, human-readable summary, e.g. "2.4 s · 312 tok · 130 tok/s", marked
 * "≈" when it's an average. `compact` leaves out the token count.
 */
export function formatStats(
  stats: ResponseStats | undefined,
  compact = false,
): string {
  if (!stats) return "";
  const parts: string[] = [];
  if (stats.latency_ms !== undefined)
    parts.push(
      stats.latency_ms < 1000
        ? `${stats.latency_ms} ms`
        : `${(stats.latency_ms / 1000).toFixed(1)} s`,
    );
  if (stats.output_tokens !== undefined && !compact)
    parts.push(`${stats.output_tokens} tok`);
  if (stats.tokens_per_s !== undefined)
    parts.push(`${Math.round(stats.tokens_per_s)} tok/s`);
  // Measured energy as is; an estimate by the middle of its range (as
  // EcoLogits' own mean), marked "~", with the range in the tooltip
  if (stats.energy_wh !== undefined) parts.push(formatEnergy(stats.energy_wh));
  else if (stats.est_energy_wh !== undefined)
    parts.push(
      `~${formatEnergy((stats.est_energy_wh.min + stats.est_energy_wh.max) / 2)}`,
    );
  const summary = parts.join(" · ");
  return stats.averaged_over && summary ? `≈ ${summary}` : summary;
}

/** Each stat on its own line with its full name, for a tooltip. */
export function describeStats(stats: ResponseStats | undefined): string[] {
  if (!stats) return [];
  const lines: string[] = [];
  if (stats.latency_ms !== undefined)
    lines.push(`Latency: ${(stats.latency_ms / 1000).toFixed(2)} s`);
  if (stats.ttft_ms !== undefined)
    lines.push(
      `Before output: ${(stats.ttft_ms / 1000).toFixed(2)} s (loading the model and reading the prompt)`,
    );
  if (stats.input_tokens !== undefined)
    lines.push(`Input tokens: ${stats.input_tokens}`);
  if (stats.output_tokens !== undefined)
    lines.push(`Output tokens: ${stats.output_tokens}`);
  if (stats.tokens_per_s !== undefined)
    lines.push(
      `Speed: ${stats.tokens_per_s} tokens/s (output tokens over latency)`,
    );
  if (stats.decode_tokens_per_s !== undefined)
    lines.push(
      `Decoding speed: ${stats.decode_tokens_per_s} tokens/s (measured by the server)`,
    );
  if (stats.cost_usd !== undefined)
    lines.push(`Cost: ${formatCost(stats.cost_usd)}`);
  if (stats.est_energy_wh !== undefined)
    lines.push(
      `Energy: ${formatEnergyRange(stats.est_energy_wh)} (estimated by EcoLogits)`,
    );
  if (stats.energy_wh !== undefined)
    lines.push(...describeMeasuredEnergy(stats));
  if (stats.averaged_over)
    lines.push(
      `≈ Averages: the provider reported one total for ${stats.averaged_over} responses`,
    );
  return lines;
}

// What the parts of the machine are called, from the energy counters' names
const ENERGY_PARTS: Dict<string> = {
  gpu: "GPU",
  cpu: "CPU",
  dram: "memory",
  ane: "Neural Engine",
};

/**
 * The power settings a measurement was taken under, in words, e.g. "on
 * battery, in Low Power Mode", "on AC power, in Automatic power mode, while
 * the Mac was hot (serious)". Undefined if unknown.
 */
export function describeConditions(
  conditions: Dict<string> | undefined,
): string | undefined {
  if (!conditions) return undefined;
  const parts: string[] = [];
  const source = conditions.power_source;
  if (source) parts.push(source === "battery" ? "on battery" : `on ${source}`);
  const mode = conditions.power_mode;
  if (mode)
    parts.push(
      mode === "Automatic" ? "in Automatic power mode" : `in ${mode} Mode`,
    );
  const thermal = conditions.thermal;
  if (thermal && thermal !== "nominal" && thermal !== "unknown")
    parts.push(`while the Mac was hot (${thermal})`);
  return parts.length > 0 ? parts.join(", ") : undefined;
}

/** The tooltip lines for energy measured on this machine. */
function describeMeasuredEnergy(stats: ResponseStats): string[] {
  const wh = stats.energy_wh as number;
  const noise =
    stats.energy_noise_wh !== undefined && stats.energy_noise_wh > 0
      ? `, ± ${formatEnergy(stats.energy_noise_wh)}`
      : "";
  const lines = [
    `Energy: ${formatEnergy(wh)} (measured on this machine, above idle power${noise})`,
  ];
  // The parts, largest first, in the energy's unit; tiny ones left out
  const parts = Object.entries(stats.energy_parts_wh ?? {})
    .filter(([, v]) => v > 0 && v >= wh * 0.005)
    .sort(([, a], [, b]) => b - a);
  if (parts.length > 0) {
    // In the same unit as the total above
    const [unit, scale] = energyUnit(wh);
    lines.push(
      `  ${parts
        .map(([c, v]) => `${ENERGY_PARTS[c] ?? c} ${twoDigits(v * scale)}`)
        .join(" · ")} ${unit}`,
    );
  }
  const conditions = describeConditions(stats.energy_conditions);
  if (conditions) lines.push(`  Measured ${conditions}`);
  if (stats.energy_conditions_changed)
    lines.push("  The power settings changed during this request");
  if (stats.energy_baseline_before_change)
    lines.push("  Idle power is from before the power settings changed");
  if (stats.energy_shared)
    lines.push("  Shared with requests generating at the same time");
  if (stats.energy_includes_load)
    lines.push(
      "  Over the whole request, as the server reports no timings: includes loading the model, if it wasn't loaded",
    );
  if (stats.load_energy_wh !== undefined)
    lines.push(
      `Loading the model: ${formatEnergy(stats.load_energy_wh)} (not included above)`,
    );
  return lines;
}

/**
 * A cost in US dollars, legibly at any size: "$1.24", "$0.0031", "$0.0000217".
 * Below a cent, three significant digits.
 */
export function formatCost(usd: number): string {
  if (usd === 0) return "$0";
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  const digits = Math.max(2, 2 - Math.floor(Math.log10(usd)));
  return `$${Number(usd.toPrecision(3)).toFixed(digits).replace(/0+$/, "")}`;
}

/** The unit that keeps an energy of `wh` Wh to a few digits: mWh below 1 Wh, kWh from 1,000. */
function energyUnit(wh: number): [string, number] {
  if (wh < 1) return ["mWh", 1000];
  if (wh >= 1000) return ["kWh", 0.001];
  return ["Wh", 1];
}

/** A number to two significant digits, e.g. 7.8, 20, 0.53. */
const twoDigits = (x: number) =>
  x === 0 ? "0" : String(Number(x.toPrecision(2)));

/** An energy given in Wh, in the unit that suits its size, e.g. "14 mWh", "1.8 Wh". */
export function formatEnergy(wh: number): string {
  const [unit, scale] = energyUnit(wh);
  return `${twoDigits(wh * scale)} ${unit}`;
}

/**
 * An estimated energy range given in Wh, both ends in the unit that suits
 * the larger, e.g. "7.8–20 mWh", or "23 mWh" when it isn't a range.
 */
export function formatEnergyRange(range: { min: number; max: number }): string {
  const [unit, scale] = energyUnit(range.max);
  const min = twoDigits(range.min * scale);
  const max = twoDigits(range.max * scale);
  return min === max ? `${min} ${unit}` : `${min}–${max} ${unit}`;
}

// The labs of OpenRouter model IDs that are EcoLogits providers
const OPENROUTER_LABS: Dict<string> = {
  openai: "openai",
  anthropic: "anthropic",
  google: "google_genai",
  mistralai: "mistralai",
  cohere: "cohere",
};

/**
 * The EcoLogits provider and model name of a ChainForge model, where EcoLogits
 * has the model. EcoLogits estimates for its providers' data centres, so local
 * models, and providers it doesn't cover, have none.
 */
export function ecologitsModel(
  llm: string,
  provider: LLMProvider | undefined,
): [string, string] | undefined {
  const candidates: [string, string][] = [];
  if (provider === LLMProvider.OpenAI) candidates.push(["openai", llm]);
  else if (provider === LLMProvider.Anthropic)
    candidates.push(["anthropic", llm]);
  else if (provider === LLMProvider.Google)
    candidates.push(["google_genai", llm.replace(/^models\//, "")]);
  else if (provider === LLMProvider.HuggingFace)
    // A ":provider" suffix picks which Inference Provider serves the model
    candidates.push([
      "huggingface_hub",
      stripHuggingFacePrefix(llm).split(":")[0],
    ]);
  else if (
    provider === LLMProvider.OpenRouter &&
    !isOpenRouterImageModel(llm)
  ) {
    // e.g. "anthropic/claude-sonnet-4.5", or "meta-llama/llama-3.1-8b-instruct:free"
    const id = stripOpenRouterPrefix(llm).split(":")[0];
    const slash = id.indexOf("/");
    const lab = OPENROUTER_LABS[id.substring(0, slash).toLowerCase()];
    if (lab) candidates.push([lab, id.substring(slash + 1)]);
    // Open-weights models go by their Hugging Face ID
    candidates.push(["huggingface_hub", id]);
  }
  return candidates.find(([p, name]) => findModel(p, name) !== undefined);
}

/**
 * EcoLogits' energy estimate for a model's requests, or undefined where
 * EcoLogits doesn't cover the model. The estimate is a nicety: if it fails,
 * the response keeps its other stats rather than being lost.
 */
export function energyEstimator(
  llm: string,
  provider: LLMProvider | undefined,
): EnergyEstimator | undefined {
  const model = ecologitsModel(llm, provider);
  if (!model) return undefined;
  const [ecoProvider, name] = model;
  return (outputTokens, latencyMs) => {
    try {
      return estimateEnergyWh(
        ecoProvider,
        name,
        outputTokens,
        latencyMs / 1000,
      );
    } catch (err) {
      console.warn(`Could not estimate the energy of ${llm}:`, err);
      return undefined;
    }
  };
}

/** A response stat the Vis Node can plot in place of an evaluator's score. */
export interface PlottableStat {
  /** The Vis Node's name for it, unlike any evaluator's score name. */
  key: string;
  /** What the axis and menu call it. */
  label: string;
  value: (s: ResponseStats) => number | undefined;
  /** The response's value, for when hovering over its point. */
  describe: (s: ResponseStats) => string;
}

const plainStat = (
  key: string,
  label: string,
  value: (s: ResponseStats) => number | undefined,
): PlottableStat => ({
  key,
  label,
  value,
  describe: (s) => `${label}: ${value(s)}`,
});

const msToS = (ms: number | undefined) =>
  ms === undefined ? undefined : ms / 1000;

export const PLOTTABLE_STATS: PlottableStat[] = [
  plainStat("__stat_latency_s", "Latency (s)", (s) => msToS(s.latency_ms)),
  plainStat("__stat_ttft_s", "Before output (s)", (s) => msToS(s.ttft_ms)),
  plainStat("__stat_input_tokens", "Input tokens", (s) => s.input_tokens),
  plainStat("__stat_output_tokens", "Output tokens", (s) => s.output_tokens),
  plainStat("__stat_tokens_per_s", "Speed (tokens/s)", (s) => s.tokens_per_s),
  plainStat(
    "__stat_decode_tokens_per_s",
    "Decoding speed (tokens/s)",
    (s) => s.decode_tokens_per_s,
  ),
  {
    ...plainStat("__stat_cost_usd", "Cost ($)", (s) => s.cost_usd),
    describe: (s) => `Cost: ${formatCost(s.cost_usd ?? 0)}`,
  },
  {
    key: "__stat_energy_mwh",
    label: "Energy, measured (mWh)",
    value: (s) => (s.energy_wh === undefined ? undefined : s.energy_wh * 1000),
    describe: (s) =>
      s.energy_wh === undefined
        ? ""
        : `Energy: ${formatEnergy(s.energy_wh)} (measured, above idle)`,
  },
  {
    // One number per response: the middle of the estimate's range, as on the
    // response's stats label. Hovering shows the range.
    key: "__stat_est_energy_mwh",
    label: "Energy, estimated (mWh)",
    value: (s) =>
      s.est_energy_wh === undefined
        ? undefined
        : ((s.est_energy_wh.min + s.est_energy_wh.max) / 2) * 1000,
    describe: (s) =>
      s.est_energy_wh === undefined
        ? ""
        : `Energy: ${formatEnergyRange(s.est_energy_wh)} (estimated by EcoLogits)`,
  },
];

/** The plottable stat with this key, if it is one. */
export function plottableStat(key: string): PlottableStat | undefined {
  return PLOTTABLE_STATS.find((s) => s.key === key);
}

type WithStats = {
  responses: LLMResponseData[];
  stats?: (ResponseStats | null)[];
  reasoning?: unknown[];
  reasoning_state?: unknown[];
};

/** The stats that at least one of these responses has, in PLOTTABLE_STATS's order. */
export function plottableStatsIn(resps: WithStats[]): PlottableStat[] {
  return PLOTTABLE_STATS.filter((stat) =>
    resps.some((r) =>
      r.stats?.some((s) => s !== null && stat.value(s) !== undefined),
    ),
  );
}

/**
 * Response objects with one stat in place of evaluation scores, so anything
 * that plots scores can plot it. Responses without the stat are left out
 * (along with their reasoning, so what's kept stays in step), and `hover`
 * gives each kept response's value, described, in the same order. `missing`
 * lists the response objects that had responses left out.
 */
export function statsAsScores<T extends WithStats>(
  resps: T[],
  stat: PlottableStat,
): {
  responses: (T & { eval_res: EvaluationResults })[];
  hover: Map<T, string[]>;
  kept: number;
  total: number;
  missing: T[];
} {
  const hover = new Map<T, string[]>();
  const kept: (T & { eval_res: EvaluationResults })[] = [];
  const missing: T[] = [];
  let total = 0;
  resps.forEach((r) => {
    total += r.responses.length;
    const idxs = r.responses
      .map((_, i) => i)
      .filter((i) => {
        const s = r.stats?.[i];
        return s != null && stat.value(s) !== undefined;
      });
    if (idxs.length < r.responses.length) missing.push(r);
    if (idxs.length === 0) return;
    const pick = <V>(arr: V[]) => idxs.map((i) => arr[i]);
    const stats = pick(r.stats ?? []) as ResponseStats[];
    const obj: T & { eval_res: EvaluationResults } = {
      ...r,
      responses: pick(r.responses),
      stats,
      eval_res: {
        dtype: "Numeric",
        items: stats.map((s) => stat.value(s) as number),
      },
    };
    // Every other per-response list, e.g. each response's reasoning
    if (Array.isArray(r.reasoning)) obj.reasoning = pick(r.reasoning);
    if (Array.isArray(r.reasoning_state))
      obj.reasoning_state = pick(r.reasoning_state);
    kept.push(obj);
    hover.set(obj, stats.map(stat.describe));
  });
  return {
    responses: kept,
    hover,
    kept: kept.reduce((n, r) => n + r.responses.length, 0),
    total,
    missing,
  };
}

/**
 * Whether EcoLogits covers the model that gave a response, i.e. whether
 * responses from it get energy estimates. Undefined when the response doesn't
 * say which model it was (only a name).
 */
export function ecologitsCovers(llm: unknown): boolean | undefined {
  const model =
    llm !== null && typeof llm === "object" ? (llm as Dict).model : undefined;
  if (typeof model !== "string") return undefined;
  return ecologitsModel(model, getProvider(model as LLM)) !== undefined;
}
